import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import VmcOrderForm from "../../components/vmcOrders/VmcOrderForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearCurrentVmcOrder,
  clearVmcOrderPartyInfo,
  fetchVmcOrderDetail,
  fetchVmcOrderParties,
  fetchVmcOrderPartyInfo,
  getCurrentVmcOrder,
  getCurrentVmcOrderLoading,
  getVmcOrderPartyInfo,
  getVmcOrderPartyInfoLoading,
  getVmcOrderPartyOptions,
} from "../../features/vmcOrders/vmcOrdersSlice";
import { PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { vmcOrderLinePayload } from "../../static/vmcLine";
import { BreadcrumbType } from "../../types/common";
import { IVmcOrderFormInput } from "../../types/vmcOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "VMC ઓર્ડર", href: route.vmcOrders },
  { label: "ઓર્ડર સુધારો" },
];

const VmcOrderEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentOrder = useAppSelector(getCurrentVmcOrder);
  const currentOrderLoading = useAppSelector(getCurrentVmcOrderLoading);
  const partyOptions = useAppSelector(getVmcOrderPartyOptions);
  const partyInfo = useAppSelector(getVmcOrderPartyInfo);
  const partyInfoLoading = useAppSelector(getVmcOrderPartyInfoLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.vmcRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderId = currentOrder?.id;
  const orderPartyId = currentOrder?.party_id;

  useEffect(() => {
    if (id) {
      dispatch(fetchVmcOrderDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentVmcOrder());
      dispatch(clearVmcOrderPartyInfo());
    };
  }, [id, dispatch]);

  // The order's own party stays in the list, and its own lines count as pieces ready for it.
  useEffect(() => {
    if (orderId && orderPartyId) {
      dispatch(fetchVmcOrderParties(orderPartyId));
      dispatch(
        fetchVmcOrderPartyInfo({ party_id: orderPartyId, order_id: orderId })
      );
    }
  }, [orderId, orderPartyId, dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(
          fetchVmcOrderPartyInfo({ party_id: partyId, order_id: Number(id) })
        );
      } else {
        dispatch(clearVmcOrderPartyInfo());
      }
    },
    [id, dispatch]
  );

  const handleSubmit = async (data: IVmcOrderFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/vmc-orders/${id}`, {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: vmcOrderLinePayload(data.items, withRates),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.vmcOrders);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo((): IVmcOrderFormInput | undefined => {
    if (!currentOrder) return undefined;
    return {
      party_id: currentOrder.party_id,
      date: moment(currentOrder.date).format("YYYY-MM-DDTHH:mm"),
      items: (currentOrder.items || []).map((item) => ({
        item_id: item.item_id,
        size_id: item.size_id,
        source: item.source,
        qty: String(item.qty),
        rate: rateText(item.rate),
        refresh: false,
      })),
    };
  }, [currentOrder]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>VMC ઓર્ડર સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentOrderLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentOrder && defaultValues ? (
          <VmcOrderForm
            key={currentOrder.id}
            defaultValues={defaultValues}
            partyOptions={partyOptions}
            partyInfo={partyInfo}
            partyInfoLoading={partyInfoLoading}
            savedOrder={currentOrder}
            partyLocked={(currentOrder.receives || []).length > 0}
            onPartyChange={handlePartyChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(route.vmcOrders)}
            submitLabel="સાચવો"
            withRates={withRates}
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            VMC ઓર્ડર ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default VmcOrderEditPage;
